import { api, QueryParams } from './api';
export type { PaginatedResponse } from './api';
import type {
  User,
  LoginCredentials,
  RegisterData,
  AuthTokens,
  Tenant,
  Partner,
  PartnerContact,
  PartnerAddress,
  Account,
  JournalEntry,
  Journal,
  Tax,
  FiscalYear,
  Payment,
  Product,
  ProductCategory,
  UnitOfMeasure,
  Warehouse,
  WarehouseLocation,
  StockMove,
  StockLot,
  SalesOrder,
  SalesInvoice,
  Delivery,
  PurchaseOrder,
  PurchaseInvoice,
  Reception,
  Employee,
  Department,
  Position,
  Contract,
  Payslip,
  Leave,
  Attendance,
  POSConfig,
  POSSession,
  POSOrder,
  BillOfMaterials,
  WorkCenter,
  ManufacturingOrder,
  Project,
  Task,
  Timesheet,
  Folder,
  Document,
  DocumentTag,
  DashboardStats,
} from '@/types';

// ============================================
// AUTH SERVICE
// ============================================

export const authService = {
  login: (credentials: LoginCredentials) =>
    api.post<{ user: User; tokens: AuthTokens }>('/auth/login', credentials),
  
  register: (data: RegisterData) =>
    api.post<{ user: User; tokens: AuthTokens }>('/auth/register', data),
  
  logout: () =>
    api.post<void>('/auth/logout'),
  
  refreshToken: (refreshToken: string) =>
    api.post<AuthTokens>('/auth/refresh', { refreshToken }),
  
  forgotPassword: (email: string) =>
    api.post<void>('/auth/forgot-password', { email }),
  
  resetPassword: (token: string, password: string) =>
    api.post<void>('/auth/reset-password', { token, password }),
  
  verifyEmail: (token: string) =>
    api.post<void>('/auth/verify-email', { token }),
  
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<void>('/auth/change-password', { currentPassword, newPassword }),
  
  getProfile: async () => {
    const response = await api.get<{ user: User; tenant: any }>('/auth/me');
    return response.user;
  },
  
  updateProfile: (data: Partial<User>) =>
    api.put<User>('/auth/me', data),
};

// ============================================
// TENANT SERVICE
// ============================================

export const tenantService = {
  getCurrent: () =>
    api.get<Tenant>('/tenant'),
  
  update: (data: Partial<Tenant>) =>
    api.put<Tenant>('/tenant', data),
  
  getSettings: () =>
    api.get<Tenant['settings']>('/tenant/settings'),
  
  updateSettings: (data: Partial<Tenant['settings']>) =>
    api.put<Tenant['settings']>('/tenant/settings', data),
};

// ============================================
// USER SERVICE
// ============================================

export const userService = {
  getAll: (params?: QueryParams) =>
    api.getPaginated<User>('/users', params),
  
  getById: (id: string) =>
    api.get<User>(`/users/${id}`),
  
  create: (data: Partial<User>) =>
    api.post<User>('/users', data),
  
  update: (id: string, data: Partial<User>) =>
    api.put<User>(`/users/${id}`, data),
  
  delete: (id: string) =>
    api.delete<void>(`/users/${id}`),
  
  updateRole: (id: string, role: string) =>
    api.patch<User>(`/users/${id}/role`, { role }),
  
  getPermissions: (id: string) =>
    api.get<{
      role: any;
      rolePermissions: string[];
      customPermissions: string[];
      effectivePermissions: string[];
    }>(`/users/${id}/permissions`),
  
  updatePermissions: (id: string, customPermissions: string[]) =>
    api.put<{ customPermissions: string[] }>(`/users/${id}/permissions`, { customPermissions }),
  
  getAvailablePermissions: () =>
    api.get<{
      categories: Record<string, string>;
      permissions: Record<string, string>;
      permissionsByCategory: Record<string, Array<{ key: string; label: string }>>;
    }>('/users/permissions/available'),
  
  verifyEmail: (id: string) =>
    api.post<User>(`/users/${id}/verify-email`, {}),
  
  updateStatus: (id: string, status: 'active' | 'inactive' | 'suspended') =>
    api.patch<User>(`/users/${id}/status`, { status }),
};

// ============================================
// PARTNER SERVICE
// ============================================

export const partnerService = {
  getAll: (params?: QueryParams) =>
    api.getPaginated<Partner>('/partners', params),
  
  getById: (id: string) =>
    api.get<Partner>(`/partners/${id}`),
  
  create: (data: Partial<Partner>) =>
    api.post<Partner>('/partners', data),
  
  update: (id: string, data: Partial<Partner>) =>
    api.put<Partner>(`/partners/${id}`, data),
  
  delete: (id: string) =>
    api.delete<void>(`/partners/${id}`),
  
  getStats: () =>
    api.get<{
      total: number;
      customers: number;
      suppliers: number;
      active: number;
      inactive: number;
    }>('/partners/stats'),
  
  getCustomers: (params?: QueryParams) =>
    api.getPaginated<Partner>('/partners', { ...params, isCustomer: true }),
  
  getSuppliers: (params?: QueryParams) =>
    api.getPaginated<Partner>('/partners', { ...params, isSupplier: true }),
  
  getBalance: (id: string) =>
    api.get<{ receivable: number; payable: number; balance: number }>(`/partners/${id}/balance`),
  
  // Contacts
  addContact: (partnerId: string, data: Partial<PartnerContact>) =>
    api.post<PartnerContact>(`/partners/${partnerId}/contacts`, data),
  
  updateContact: (partnerId: string, contactId: string, data: Partial<PartnerContact>) =>
    api.put<PartnerContact>(`/partners/${partnerId}/contacts/${contactId}`, data),
  
  deleteContact: (partnerId: string, contactId: string) =>
    api.delete<void>(`/partners/${partnerId}/contacts/${contactId}`),
  
  // Addresses
  addAddress: (partnerId: string, data: Partial<PartnerAddress>) =>
    api.post<PartnerAddress>(`/partners/${partnerId}/addresses`, data),
  
  updateAddress: (partnerId: string, addressId: string, data: Partial<PartnerAddress>) =>
    api.put<PartnerAddress>(`/partners/${partnerId}/addresses/${addressId}`, data),
  
  deleteAddress: (partnerId: string, addressId: string) =>
    api.delete<void>(`/partners/${partnerId}/addresses/${addressId}`),
  
  // Transactions
  getTransactions: (partnerId: string, params?: QueryParams) =>
    api.getPaginated<any>(`/partners/${partnerId}/transactions`, params),
};

// ============================================
// ACCOUNTING SERVICE
// ============================================

export const accountingService = {
  // Accounts
  accounts: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<Account>('/accounting/accounts', params),
    
    getById: (id: string) =>
      api.get<Account>(`/accounting/accounts/${id}`),
    
    create: (data: Partial<Account>) =>
      api.post<Account>('/accounting/accounts', data),
    
    update: (id: string, data: Partial<Account>) =>
      api.put<Account>(`/accounting/accounts/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/accounting/accounts/${id}`),
    
    getBalance: (id: string, params?: { startDate?: string; endDate?: string }) =>
      api.get<{ debit: number; credit: number; balance: number }>(
        `/accounting/accounts/${id}/balance`,
        params
      ),
    
    getStats: () =>
      api.get<{ total: number; asset: number; liability: number; equity: number; income: number; expense: number }>('/accounting/accounts/stats'),
  },
  
  // Invoices
  invoices: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<any>('/accounting/invoices', params),
    
    getById: (id: string) =>
      api.get<any>(`/accounting/invoices/${id}`),
    
    create: (data: any) =>
      api.post<any>('/accounting/invoices', data),
    
    update: (id: string, data: any) =>
      api.put<any>(`/accounting/invoices/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/accounting/invoices/${id}`),
    
    getStats: () =>
      api.get<{ total: number; draft: number; sent: number; paid: number; overdue: number }>('/accounting/invoices/stats'),
    
    send: (id: string) =>
      api.post<any>(`/accounting/invoices/${id}/send`),
    
    markAsPaid: (id: string) =>
      api.post<any>(`/accounting/invoices/${id}/mark-paid`),
  },
  
  // Journal Entries
  journalEntries: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<JournalEntry>('/accounting/journal-entries', params),
    
    getById: (id: string) =>
      api.get<JournalEntry>(`/accounting/journal-entries/${id}`),
    
    create: (data: Partial<JournalEntry>) =>
      api.post<JournalEntry>('/accounting/journal-entries', data),
    
    update: (id: string, data: Partial<JournalEntry>) =>
      api.put<JournalEntry>(`/accounting/journal-entries/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/accounting/journal-entries/${id}`),
    
    post: (id: string) =>
      api.post<JournalEntry>(`/accounting/journal-entries/${id}/post`),
    
    reverse: (id: string) =>
      api.post<JournalEntry>(`/accounting/journal-entries/${id}/reverse`),
    
    validate: (id: string) =>
      api.post<JournalEntry>(`/accounting/journal-entries/${id}/validate`),
    
    getStats: () =>
      api.get<{ total: number; draft: number; posted: number; validated: number }>('/accounting/journal-entries/stats'),
  },
  
  // Journals
  journals: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<Journal>('/accounting/journals', params),
    
    getById: (id: string) =>
      api.get<Journal>(`/accounting/journals/${id}`),
    
    create: (data: Partial<Journal>) =>
      api.post<Journal>('/accounting/journals', data),
    
    update: (id: string, data: Partial<Journal>) =>
      api.put<Journal>(`/accounting/journals/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/accounting/journals/${id}`),
  },
  
  // Taxes
  taxes: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<Tax>('/accounting/taxes', params),
    
    getById: (id: string) =>
      api.get<Tax>(`/accounting/taxes/${id}`),
    
    create: (data: Partial<Tax>) =>
      api.post<Tax>('/accounting/taxes', data),
    
    update: (id: string, data: Partial<Tax>) =>
      api.put<Tax>(`/accounting/taxes/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/accounting/taxes/${id}`),
  },
  
  // Fiscal Years
  fiscalYears: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<FiscalYear>('/accounting/fiscal-years', params),
    
    getById: (id: string) =>
      api.get<FiscalYear>(`/accounting/fiscal-years/${id}`),
    
    create: (data: Partial<FiscalYear>) =>
      api.post<FiscalYear>('/accounting/fiscal-years', data),
    
    update: (id: string, data: Partial<FiscalYear>) =>
      api.put<FiscalYear>(`/accounting/fiscal-years/${id}`, data),
    
    close: (id: string) =>
      api.post<FiscalYear>(`/accounting/fiscal-years/${id}/close`),
  },
  
  // Payments
  payments: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<Payment>('/accounting/payments', params),
    
    getById: (id: string) =>
      api.get<Payment>(`/accounting/payments/${id}`),
    
    create: (data: Partial<Payment>) =>
      api.post<Payment>('/accounting/payments', data),
    
    update: (id: string, data: Partial<Payment>) =>
      api.put<Payment>(`/accounting/payments/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/accounting/payments/${id}`),
    
    post: (id: string) =>
      api.post<Payment>(`/accounting/payments/${id}/post`),
    
    cancel: (id: string) =>
      api.post<Payment>(`/accounting/payments/${id}/cancel`),
    
    getStats: () =>
      api.get<{
        totalIncoming: number;
        totalOutgoing: number;
        netBalance: number;
        pendingCount: number;
        incomingTrend: number;
        outgoingTrend: number;
      }>('/accounting/payments/stats'),
  },
  
  // TODO: Add bank accounts and invoices services when accounting module is complete
  // bankAccounts: {...},
  // invoices: {...},
  
  // Reports
  reports: {
    generate: (reportType: string, params?: { startDate?: string; endDate?: string }) => {
      // Map report types to actual backend routes
      const routeMap: Record<string, string> = {
        'balance_sheet': '/accounting/accounts/balance-sheet',
        'income_statement': '/accounting/accounts/income-statement',
        'trial_balance': '/accounting/accounts/trial-balance',
        'general_ledger': '/accounting/journal-entries',
        'vat_report': '/accounting/taxes/report',
        'aged_receivables': '/partners/aged-receivables',
        'aged_payables': '/partners/aged-payables',
      };
      const route = routeMap[reportType] || `/accounting/reports/${reportType}`;
      return api.get<any>(route, params);
    },
    
    trialBalance: (params?: { fiscalYear?: string; date?: string }) =>
      api.get<any>('/accounting/accounts/trial-balance', params),
    
    generalLedger: (params?: { account?: string; startDate?: string; endDate?: string }) =>
      api.get<any>('/accounting/journal-entries', params),
    
    incomeStatement: (params?: { startDate?: string; endDate?: string }) =>
      api.get<any>('/accounting/accounts/income-statement', params),
    
    balanceSheet: (params?: { date?: string }) =>
      api.get<any>('/accounting/accounts/balance-sheet', params),
    
    tvaReport: (params?: { month?: number; year?: number }) =>
      api.get<any>('/accounting/taxes/report', params),
  },
};

// ============================================
// INVENTORY SERVICE
// ============================================

export const inventoryService = {
  // Products
  products: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<Product>('/inventory/products', params),
    
    getById: (id: string) =>
      api.get<Product>(`/inventory/products/${id}`),
    
    create: (data: Partial<Product>) =>
      api.post<Product>('/inventory/products', data),
    
    update: (id: string, data: Partial<Product>) =>
      api.put<Product>(`/inventory/products/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/inventory/products/${id}`),
    
    getStock: (id: string) =>
      api.get<any>(`/inventory/products/${id}/stock`),
    
    adjustStock: (id: string, data: { location: string; quantity: number; reason: string }) =>
      api.post<any>(`/inventory/products/${id}/adjust-stock`, data),
  },
  
  // Categories
  categories: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<ProductCategory>('/inventory/categories', params),
    
    getById: (id: string) =>
      api.get<ProductCategory>(`/inventory/categories/${id}`),
    
    create: (data: Partial<ProductCategory>) =>
      api.post<ProductCategory>('/inventory/categories', data),
    
    update: (id: string, data: Partial<ProductCategory>) =>
      api.put<ProductCategory>(`/inventory/categories/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/inventory/categories/${id}`),
  },
  
  // Warehouses
  warehouses: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<Warehouse>('/inventory/stock/warehouses', params),

    getById: (id: string) =>
      api.get<Warehouse>(`/inventory/stock/warehouses/${id}`),

    create: (data: Partial<Warehouse>) =>
      api.post<Warehouse>('/inventory/stock/warehouses', data),

    update: (id: string, data: Partial<Warehouse>) =>
      api.put<Warehouse>(`/inventory/stock/warehouses/${id}`, data),

    delete: (id: string) =>
      api.delete<void>(`/inventory/stock/warehouses/${id}`),
  },

  // Locations
  locations: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<WarehouseLocation>('/inventory/locations', params),
    
    getById: (id: string) =>
      api.get<WarehouseLocation>(`/inventory/locations/${id}`),
    
    create: (data: Partial<WarehouseLocation>) =>
      api.post<WarehouseLocation>('/inventory/locations', data),
    
    update: (id: string, data: Partial<WarehouseLocation>) =>
      api.put<WarehouseLocation>(`/inventory/locations/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/inventory/locations/${id}`),
  },
  
  // Stock Moves
  stockMoves: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<StockMove>('/inventory/stock/moves', params),

    getById: (id: string) =>
      api.get<StockMove>(`/inventory/stock/moves/${id}`),

    create: (data: Partial<StockMove>) =>
      api.post<StockMove>('/inventory/stock/moves', data),

    confirm: (id: string) =>
      api.post<StockMove>(`/inventory/stock/moves/${id}/confirm`),

    done: (id: string) =>
      api.post<StockMove>(`/inventory/stock/moves/${id}/done`),

    cancel: (id: string) =>
      api.post<StockMove>(`/inventory/stock/moves/${id}/cancel`),
  },

  // Stock
  stock: {
    getWarehouseDetails: (id: string) =>
      api.get<{
        warehouse: Warehouse;
        stockSummary: {
          totalProducts: number;
          totalQuantity: number;
          totalValue: number;
        };
        locations: WarehouseLocation[];
      }>(`/inventory/stock/warehouses/${id}`),
  },

  // Lots
  lots: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<StockLot>('/inventory/lots', params),
    
    getById: (id: string) =>
      api.get<StockLot>(`/inventory/lots/${id}`),
    
    create: (data: Partial<StockLot>) =>
      api.post<StockLot>('/inventory/lots', data),
    
    update: (id: string, data: Partial<StockLot>) =>
      api.put<StockLot>(`/inventory/lots/${id}`, data),
  },

  // Units of Measure
  uom: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<UnitOfMeasure>('/inventory/uom', params),
    
    getById: (id: string) =>
      api.get<UnitOfMeasure>(`/inventory/uom/${id}`),
    
    create: (data: Partial<UnitOfMeasure>) =>
      api.post<UnitOfMeasure>('/inventory/uom', data),
    
    update: (id: string, data: Partial<UnitOfMeasure>) =>
      api.put<UnitOfMeasure>(`/inventory/uom/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/inventory/uom/${id}`),
  },
};

// Alias for compatibility
export const warehouseService = inventoryService;

// ============================================
// SALES SERVICE
// ============================================

export const salesService = {
  // Orders
  orders: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<SalesOrder>('/sales/orders', params),
    
    getById: (id: string) =>
      api.get<SalesOrder>(`/sales/orders/${id}`),
    
    create: (data: Partial<SalesOrder>) =>
      api.post<SalesOrder>('/sales/orders', data),
    
    update: (id: string, data: Partial<SalesOrder>) =>
      api.put<SalesOrder>(`/sales/orders/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/sales/orders/${id}`),
    
    confirm: (id: string) =>
      api.post<SalesOrder>(`/sales/orders/${id}/confirm`),
    
    cancel: (id: string) =>
      api.post<SalesOrder>(`/sales/orders/${id}/cancel`),
    
    createInvoice: (id: string) =>
      api.post<SalesInvoice>(`/sales/orders/${id}/create-invoice`),
    
    createDelivery: (id: string) =>
      api.post<Delivery>(`/sales/orders/${id}/create-delivery`),
  },
  
  // Invoices
  invoices: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<SalesInvoice>('/sales/invoices', params),
    
    getById: (id: string) =>
      api.get<SalesInvoice>(`/sales/invoices/${id}`),
    
    create: (data: Partial<SalesInvoice>) =>
      api.post<SalesInvoice>('/sales/invoices', data),
    
    update: (id: string, data: Partial<SalesInvoice>) =>
      api.put<SalesInvoice>(`/sales/invoices/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/sales/invoices/${id}`),
    
    send: (id: string) =>
      api.post<SalesInvoice>(`/sales/invoices/${id}/send`),
    
    cancel: (id: string) =>
      api.post<SalesInvoice>(`/sales/invoices/${id}/cancel`),
    
    registerPayment: (id: string, data: Partial<Payment>) =>
      api.post<Payment>(`/sales/invoices/${id}/register-payment`, data),
    
    downloadPdf: (id: string) =>
      api.download(`/sales/invoices/${id}/pdf`, `invoice-${id}.pdf`),
  },
  
  // Deliveries
  deliveries: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<Delivery>('/sales/deliveries', params),
    
    getById: (id: string) =>
      api.get<Delivery>(`/sales/deliveries/${id}`),
    
    create: (data: Partial<Delivery>) =>
      api.post<Delivery>('/sales/deliveries', data),
    
    update: (id: string, data: Partial<Delivery>) =>
      api.put<Delivery>(`/sales/deliveries/${id}`, data),
    
    confirm: (id: string) =>
      api.post<Delivery>(`/sales/deliveries/${id}/confirm`),
    
    validate: (id: string, lines: any[]) =>
      api.post<Delivery>(`/sales/deliveries/${id}/validate`, { lines }),
    
    cancel: (id: string) =>
      api.post<Delivery>(`/sales/deliveries/${id}/cancel`),
  },
};

// ============================================
// PURCHASE SERVICE
// ============================================

export const purchaseService = {
  // Orders
  orders: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<PurchaseOrder>('/purchase/orders', params),
    
    getById: (id: string) =>
      api.get<PurchaseOrder>(`/purchase/orders/${id}`),
    
    create: (data: Partial<PurchaseOrder>) =>
      api.post<PurchaseOrder>('/purchase/orders', data),
    
    update: (id: string, data: Partial<PurchaseOrder>) =>
      api.put<PurchaseOrder>(`/purchase/orders/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/purchase/orders/${id}`),
    
    send: (id: string) =>
      api.post<PurchaseOrder>(`/purchase/orders/${id}/send`),
    
    confirm: (id: string) =>
      api.post<PurchaseOrder>(`/purchase/orders/${id}/confirm`),
    
    cancel: (id: string) =>
      api.post<PurchaseOrder>(`/purchase/orders/${id}/cancel`),
    
    createInvoice: (id: string) =>
      api.post<PurchaseInvoice>(`/purchase/orders/${id}/create-invoice`),
    
    createReception: (id: string) =>
      api.post<Reception>(`/purchase/orders/${id}/create-reception`),
  },
  
  // Invoices
  invoices: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<PurchaseInvoice>('/purchase/invoices', params),
    
    getById: (id: string) =>
      api.get<PurchaseInvoice>(`/purchase/invoices/${id}`),
    
    getStats: (params?: { startDate?: string; endDate?: string }) =>
      api.get<any>('/purchase/invoices/stats', params),
    
    create: (data: Partial<PurchaseInvoice>) =>
      api.post<PurchaseInvoice>('/purchase/invoices', data),
    
    update: (id: string, data: Partial<PurchaseInvoice>) =>
      api.put<PurchaseInvoice>(`/purchase/invoices/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/purchase/invoices/${id}`),
    
    validate: (id: string) =>
      api.post<PurchaseInvoice>(`/purchase/invoices/${id}/validate`),
    
    cancel: (id: string) =>
      api.post<PurchaseInvoice>(`/purchase/invoices/${id}/cancel`),
    
    registerPayment: (id: string, data: Partial<Payment>) =>
      api.post<Payment>(`/purchase/invoices/${id}/register-payment`, data),
  },
  
  // Receptions
  receptions: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<Reception>('/purchase/receptions', params),
    
    getById: (id: string) =>
      api.get<Reception>(`/purchase/receptions/${id}`),
    
    create: (data: Partial<Reception>) =>
      api.post<Reception>('/purchase/receptions', data),
    
    update: (id: string, data: Partial<Reception>) =>
      api.put<Reception>(`/purchase/receptions/${id}`, data),
    
    confirm: (id: string) =>
      api.post<Reception>(`/purchase/receptions/${id}/confirm`),
    
    complete: (id: string) =>
      api.post<Reception>(`/purchase/receptions/${id}/complete`),
    
    validate: (id: string, lines: any[]) =>
      api.post<Reception>(`/purchase/receptions/${id}/validate`, { lines }),
    
    cancel: (id: string) =>
      api.post<Reception>(`/purchase/receptions/${id}/cancel`),
  },
};

// ============================================
// HR SERVICE
// ============================================

export const hrService = {
  // Employees
  employees: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<Employee>('/hr/employees', params),
    
    getById: (id: string) =>
      api.get<Employee>(`/hr/employees/${id}`),
    
    getStats: () =>
      api.get<any>('/hr/employees/statistics'),
    
    create: (data: Partial<Employee>) =>
      api.post<Employee>('/hr/employees', data),
    
    update: (id: string, data: Partial<Employee>) =>
      api.put<Employee>(`/hr/employees/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/hr/employees/${id}`),
    
    terminate: (id: string, data: { date: string; reason: string }) =>
      api.post<Employee>(`/hr/employees/${id}/terminate`, data),
  },
  
  // Departments
  departments: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<Department>('/hr/departments', params),
    
    getById: (id: string) =>
      api.get<Department>(`/hr/departments/${id}`),
    
    create: (data: Partial<Department>) =>
      api.post<Department>('/hr/departments', data),
    
    update: (id: string, data: Partial<Department>) =>
      api.put<Department>(`/hr/departments/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/hr/departments/${id}`),
  },
  
  // Positions
  positions: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<Position>('/hr/positions', params),
    
    getById: (id: string) =>
      api.get<Position>(`/hr/positions/${id}`),
    
    create: (data: Partial<Position>) =>
      api.post<Position>('/hr/positions', data),
    
    update: (id: string, data: Partial<Position>) =>
      api.put<Position>(`/hr/positions/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/hr/positions/${id}`),
  },
  
  // Contracts
  contracts: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<Contract>('/hr/contracts', params),
    
    getById: (id: string) =>
      api.get<Contract>(`/hr/contracts/${id}`),
    
    create: (data: Partial<Contract>) =>
      api.post<Contract>('/hr/contracts', data),
    
    update: (id: string, data: Partial<Contract>) =>
      api.put<Contract>(`/hr/contracts/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/hr/contracts/${id}`),
    
    activate: (id: string) =>
      api.post<Contract>(`/hr/contracts/${id}/activate`),
    
    terminate: (id: string, date: string) =>
      api.post<Contract>(`/hr/contracts/${id}/terminate`, { date }),
  },

  // Contract Types
  contractTypes: {
    getAll: () =>
      api.get<any[]>('/hr/contract-types'),
    
    getById: (id: string) =>
      api.get<any>(`/hr/contract-types/${id}`),
    
    create: (data: any) =>
      api.post<any>('/hr/contract-types', data),
    
    update: (id: string, data: any) =>
      api.put<any>(`/hr/contract-types/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/hr/contract-types/${id}`),
  },
  
  // Payslips
  payslips: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<Payslip>('/hr/payslips', params),
    
    getById: (id: string) =>
      api.get<Payslip>(`/hr/payslips/${id}`),
    
    create: (data: Partial<Payslip>) =>
      api.post<Payslip>('/hr/payslips', data),
    
    update: (id: string, data: Partial<Payslip>) =>
      api.put<Payslip>(`/hr/payslips/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/hr/payslips/${id}`),
    
    confirm: (id: string) =>
      api.post<Payslip>(`/hr/payslips/${id}/confirm`),
    
    pay: (id: string, data: { paymentMethod: string; paymentDate: string }) =>
      api.post<Payslip>(`/hr/payslips/${id}/pay`, data),
    
    downloadPdf: (id: string) =>
      api.download(`/hr/payslips/${id}/pdf`, `payslip-${id}.pdf`),
    
    generate: (data: { month: number; year: number; employees?: string[] }) =>
      api.post<Payslip[]>('/hr/payslips/generate', data),
  },
  
  // Payroll management (runs)
  payroll: {
    getPayslips: (params?: { month: number; year: number; search?: string; status?: string; department?: string }) =>
      api.getPaginated<Payslip>('/hr/payroll/payslips', params),
    
    getPeriodSummary: (params: { month: number; year: number }) =>
      api.get<any>(`/hr/payroll/summary?month=${params.month}&year=${params.year}`),
    
    generatePayslips: (data: { month: number; year: number; employees?: string[] }) =>
      api.post<{ count: number; payslips: Payslip[] }>('/hr/payroll/payslips/generate', data),
    
    validateAll: (data: { month: number; year: number }) =>
      api.post<void>('/hr/payroll/validate-all', data),
    
    payAll: (data: { month: number; year: number; paymentMethod: string; paymentDate: string }) =>
      api.post<void>('/hr/payroll/pay-all', data),
  },
  
  // Leaves
  leaves: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<Leave>('/hr/leaves', params),
    
    getById: (id: string) =>
      api.get<Leave>(`/hr/leaves/${id}`),
    
    getTypes: () =>
      api.get<any[]>('/hr/leaves/types'),
    
    getCalendar: (params?: { startDate: string; endDate: string }) =>
      api.get<any>('/hr/leaves/calendar', params),
    
    getStats: () =>
      api.get<any>('/hr/leaves/stats'),
    
    create: (data: Partial<Leave>) =>
      api.post<Leave>('/hr/leaves', data),
    
    update: (id: string, data: Partial<Leave>) =>
      api.put<Leave>(`/hr/leaves/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/hr/leaves/${id}`),
    
    submit: (id: string) =>
      api.post<Leave>(`/hr/leaves/${id}/submit`),
    
    approve: (id: string) =>
      api.post<Leave>(`/hr/leaves/${id}/approve`),
    
    reject: (id: string, reason: string) =>
      api.post<Leave>(`/hr/leaves/${id}/reject`, { reason }),
    
    cancel: (id: string) =>
      api.post<Leave>(`/hr/leaves/${id}/cancel`),
    
    getBalance: (employeeId: string) =>
      api.get<any>(`/hr/leaves/balance/${employeeId}`),
  },
  
  // Attendance
  attendance: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<Attendance>('/hr/attendance', params),
    
    getById: (id: string) =>
      api.get<Attendance>(`/hr/attendance/${id}`),
    
    checkIn: (employeeId: string) =>
      api.post<Attendance>('/hr/attendance/check-in', { employeeId }),
    
    checkOut: (employeeId: string) =>
      api.post<Attendance>('/hr/attendance/check-out', { employeeId }),
    
    create: (data: Partial<Attendance>) =>
      api.post<Attendance>('/hr/attendance', data),
    
    update: (id: string, data: Partial<Attendance>) =>
      api.put<Attendance>(`/hr/attendance/${id}`, data),
  },
};

// ============================================
// POS SERVICE
// ============================================

interface POSDashboardStats {
  activeSessions: number;
  todaySales: number;
  todayRefunds: number;
  todayOrders: number;
  todayRefundCount: number;
  totalDifference: number;
  closedSessions: number;
}

interface POSActiveSession {
  _id: string;
  sessionId: string;
  configId: {
    _id: string;
    name: string;
    code: string;
  };
  userId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  status: string;
  openedAt: string;
  openingCash: number;
  orderCount: number;
  totalSales: number;
  cashPayments: number;
  cardPayments: number;
}

interface POSPaymentMethod {
  _id: string;
  code: string;
  name: string;
  nameAr?: string;
  type: 'cash' | 'card' | 'mobile' | 'check' | 'credit' | 'voucher';
  openCashDrawer: boolean;
  usePaymentTerminal: boolean;
  requireApproval: boolean;
  provider?: string;
  icon?: string;
  color?: string;
  sequence: number;
  isActive: boolean;
}

export const posService = {
  // Dashboard
  dashboard: {
    getStats: () =>
      api.get<POSDashboardStats>('/pos/dashboard/stats'),
    
    getActiveSessions: () =>
      api.get<POSActiveSession[]>('/pos/dashboard/active-sessions'),
  },

  // Payment Methods
  paymentMethods: {
    getAll: () =>
      api.get<POSPaymentMethod[]>('/pos/payment-methods'),
    
    getById: (id: string) =>
      api.get<POSPaymentMethod>(`/pos/payment-methods/${id}`),
    
    create: (data: Partial<POSPaymentMethod>) =>
      api.post<POSPaymentMethod>('/pos/payment-methods', data),
    
    update: (id: string, data: Partial<POSPaymentMethod>) =>
      api.put<POSPaymentMethod>(`/pos/payment-methods/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/pos/payment-methods/${id}`),
  },

  // Configs
  configs: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<POSConfig>('/pos/configs', params),
    
    getById: (id: string) =>
      api.get<POSConfig>(`/pos/configs/${id}`),
    
    create: (data: Partial<POSConfig>) =>
      api.post<POSConfig>('/pos/configs', data),
    
    update: (id: string, data: Partial<POSConfig>) =>
      api.put<POSConfig>(`/pos/configs/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/pos/configs/${id}`),
  },
  
  // Sessions
  sessions: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<POSSession>('/pos/sessions', params),
    
    getById: (id: string) =>
      api.get<POSSession>(`/pos/sessions/${id}`),
    
    getMySession: () =>
      api.get<POSSession | null>('/pos/sessions/my-session'),
    
    getCurrent: (configId: string) =>
      api.get<POSSession | null>(`/pos/sessions/current/${configId}`),
    
    getSummary: (id: string) =>
      api.get<any>(`/pos/sessions/${id}/summary`),
    
    open: (configId: string, openingCash: number) =>
      api.post<POSSession>('/pos/sessions/open', { configId, openingCash }),
    
    close: (id: string, data: { closingCash: number; notes?: string }) =>
      api.post<POSSession>(`/pos/sessions/${id}/close`, data),
    
    validate: (id: string) =>
      api.post<POSSession>(`/pos/sessions/${id}/validate`),
  },

  // Cash Movements
  cash: {
    cashIn: (sessionId: string, data: { amount: number; reason: string }) =>
      api.post<any>('/pos/cash/in', { sessionId, ...data }),
    
    cashOut: (sessionId: string, data: { amount: number; reason: string }) =>
      api.post<any>('/pos/cash/out', { sessionId, ...data }),
  },
  
  // Orders
  orders: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<POSOrder>('/pos/orders', params),
    
    getById: (id: string) =>
      api.get<POSOrder>(`/pos/orders/${id}`),
    
    create: (data: Partial<POSOrder>) =>
      api.post<POSOrder>('/pos/orders', data),
    
    addLine: (orderId: string, data: { productId: string; quantity: number; unitPrice?: number }) =>
      api.post<POSOrder>(`/pos/orders/${orderId}/lines`, data),
    
    updateLine: (orderId: string, lineId: string, data: { quantity?: number; discount?: number }) =>
      api.put<POSOrder>(`/pos/orders/${orderId}/lines/${lineId}`, data),
    
    removeLine: (orderId: string, lineId: string) =>
      api.delete<POSOrder>(`/pos/orders/${orderId}/lines/${lineId}`),
    
    applyDiscount: (orderId: string, data: { discountPercent: number }) =>
      api.post<POSOrder>(`/pos/orders/${orderId}/discount`, data),
    
    pay: (orderId: string, payments: { method: string; amount: number; reference?: string }[]) =>
      api.post<POSOrder>(`/pos/orders/${orderId}/pay`, { payments }),
    
    refund: (id: string, reason?: string) =>
      api.post<POSOrder>(`/pos/orders/${id}/refund`, { reason }),
    
    createInvoice: (id: string) =>
      api.post<any>(`/pos/orders/${id}/invoice`),
    
    getReceipt: (id: string) =>
      api.get<any>(`/pos/orders/${id}/receipt`),
    
    getReport: (params: { startDate: string; endDate: string }) =>
      api.get<any>('/pos/orders/reports/sales', params),
    
    getSalesByProduct: (params: { startDate: string; endDate: string }) =>
      api.get<any>('/pos/orders/reports/by-product', params),
  },
};

// ============================================
// MANUFACTURING SERVICE
// ============================================

export const manufacturingService = {
  // BOMs
  boms: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<BillOfMaterials>('/manufacturing/boms', params),
    
    getById: (id: string) =>
      api.get<BillOfMaterials>(`/manufacturing/boms/${id}`),
    
    create: (data: Partial<BillOfMaterials>) =>
      api.post<BillOfMaterials>('/manufacturing/boms', data),
    
    update: (id: string, data: Partial<BillOfMaterials>) =>
      api.put<BillOfMaterials>(`/manufacturing/boms/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/manufacturing/boms/${id}`),
  },
  
  // Work Centers
  workCenters: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<WorkCenter>('/manufacturing/work-centers', params),
    
    getById: (id: string) =>
      api.get<WorkCenter>(`/manufacturing/work-centers/${id}`),
    
    create: (data: Partial<WorkCenter>) =>
      api.post<WorkCenter>('/manufacturing/work-centers', data),
    
    update: (id: string, data: Partial<WorkCenter>) =>
      api.put<WorkCenter>(`/manufacturing/work-centers/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/manufacturing/work-centers/${id}`),
  },
  
  // Manufacturing Orders
  orders: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<ManufacturingOrder>('/manufacturing/orders', params),
    
    getById: (id: string) =>
      api.get<ManufacturingOrder>(`/manufacturing/orders/${id}`),
    
    create: (data: Partial<ManufacturingOrder>) =>
      api.post<ManufacturingOrder>('/manufacturing/orders', data),
    
    update: (id: string, data: Partial<ManufacturingOrder>) =>
      api.put<ManufacturingOrder>(`/manufacturing/orders/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/manufacturing/orders/${id}`),
    
    confirm: (id: string) =>
      api.post<ManufacturingOrder>(`/manufacturing/orders/${id}/confirm`),
    
    start: (id: string) =>
      api.post<ManufacturingOrder>(`/manufacturing/orders/${id}/start`),
    
    complete: (id: string, quantityProduced: number) =>
      api.post<ManufacturingOrder>(`/manufacturing/orders/${id}/complete`, { quantityProduced }),
    
    cancel: (id: string) =>
      api.post<ManufacturingOrder>(`/manufacturing/orders/${id}/cancel`),
  },
};

// ============================================
// PROJECTS SERVICE
// ============================================

export const projectsService = {
  // Projects
  projects: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<Project>('/projects', params),
    
    getById: (id: string) =>
      api.get<Project>(`/projects/${id}`),
    
    create: (data: Partial<Project>) =>
      api.post<Project>('/projects', data),
    
    update: (id: string, data: Partial<Project>) =>
      api.put<Project>(`/projects/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/projects/${id}`),
    
    updateStatus: (id: string, status: string) =>
      api.patch<Project>(`/projects/${id}/status`, { status }),
  },
  
  // Tasks
  tasks: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<Task>('/projects/tasks', params),
    
    getById: (id: string) =>
      api.get<Task>(`/projects/tasks/${id}`),
    
    create: (data: Partial<Task>) =>
      api.post<Task>('/projects/tasks', data),
    
    update: (id: string, data: Partial<Task>) =>
      api.put<Task>(`/projects/tasks/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/projects/tasks/${id}`),
    
    updateStatus: (id: string, status: string) =>
      api.post<Task>(`/projects/tasks/${id}/status`, { status }),
    
    assign: (id: string, userId: string) =>
      api.post<Task>(`/projects/tasks/${id}/assign`, { userId }),
  },
  
  // Timesheets
  timesheets: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<Timesheet>('/projects/timesheets', params),
    
    getById: (id: string) =>
      api.get<Timesheet>(`/projects/timesheets/${id}`),
    
    create: (data: Partial<Timesheet>) =>
      api.post<Timesheet>('/projects/timesheets', data),
    
    update: (id: string, data: Partial<Timesheet>) =>
      api.put<Timesheet>(`/projects/timesheets/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/projects/timesheets/${id}`),
    
    submit: (id: string) =>
      api.post<Timesheet>(`/projects/timesheets/${id}/submit`),
    
    approve: (id: string) =>
      api.post<Timesheet>(`/projects/timesheets/${id}/approve`),
    
    reject: (id: string) =>
      api.post<Timesheet>(`/projects/timesheets/${id}/reject`),
  },
};

// ============================================
// DOCUMENTS SERVICE
// ============================================

export const documentsService = {
  // Folders
  folders: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<Folder>('/documents/folders', params),
    
    getTree: () =>
      api.get<Folder[]>('/documents/folders/tree'),
    
    getById: (id: string) =>
      api.get<Folder>(`/documents/folders/${id}`),
    
    create: (data: Partial<Folder>) =>
      api.post<Folder>('/documents/folders', data),
    
    update: (id: string, data: Partial<Folder>) =>
      api.put<Folder>(`/documents/folders/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/documents/folders/${id}`),
  },
  
  // Documents
  documents: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<Document>('/documents', params),
    
    getById: (id: string) =>
      api.get<Document>(`/documents/${id}`),
    
    upload: (file: File, folderId?: string, onProgress?: (progress: number) => void) => {
      const additionalData: Record<string, string> = {};
      if (folderId) additionalData.folderId = folderId;
      return api.upload<Document>('/documents/upload', file, onProgress, additionalData);
    },
    
    update: (id: string, data: Partial<Document>) =>
      api.put<Document>(`/documents/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/documents/${id}`),
    
    download: (id: string, filename: string) =>
      api.download(`/documents/${id}/download`, filename),
    
    share: (id: string, data: { email?: string; userId?: string; permission: string; expiresAt?: string }) =>
      api.post<Document>(`/documents/${id}/share`, data),
    
    unshare: (id: string, shareId: string) =>
      api.delete<void>(`/documents/${id}/share/${shareId}`),
  },
  
  // Tags
  tags: {
    getAll: (params?: QueryParams) =>
      api.getPaginated<DocumentTag>('/documents/tags', params),
    
    create: (data: Partial<DocumentTag>) =>
      api.post<DocumentTag>('/documents/tags', data),
    
    update: (id: string, data: Partial<DocumentTag>) =>
      api.put<DocumentTag>(`/documents/tags/${id}`, data),
    
    delete: (id: string) =>
      api.delete<void>(`/documents/tags/${id}`),
  },
};

// ============================================
// DASHBOARD SERVICE
// ============================================

export const dashboardService = {
  getStats: () =>
    api.get<DashboardStats>('/dashboard/stats'),
  
  getSalesChart: (params?: { period?: string }) =>
    api.get<any>('/dashboard/charts/sales', params),
  
  getPurchaseChart: (params?: { period?: string }) =>
    api.get<any>('/dashboard/charts/purchases', params),
  
  getCashFlowChart: (params?: { period?: string }) =>
    api.get<any>('/dashboard/charts/cash-flow', params),
  
  getTopProducts: (params?: { limit?: number }) =>
    api.get<any>('/dashboard/top-products', params),
  
  getTopCustomers: (params?: { limit?: number }) =>
    api.get<any>('/dashboard/top-customers', params),
  
  getRecentActivity: (params?: { limit?: number }) =>
    api.get<any>('/dashboard/activity', params),
  
  getNotifications: () =>
    api.get<any>('/dashboard/notifications'),
};
